import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { settingsService, SettingsData, SystemPreferences, SecuritySettings, NotificationSettings, IntegrationSettings, DocumentTemplates, BackupSettings, StaffMember } from '../services/settings.service';
import { Organization, OrganizationUpdate } from '../types/database.types';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

interface SettingsContextType {
  // Data
  settings: SettingsData | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  refreshSettings: () => Promise<void>;
  
  // Organization/Business Information
  updateOrganization: (updates: OrganizationUpdate) => Promise<void>;
  
  // System Preferences
  updateSystemPreferences: (preferences: Partial<SystemPreferences>) => Promise<void>;
  
  // Security Settings
  updateSecuritySettings: (settings: Partial<SecuritySettings>) => Promise<void>;
  
  // Notification Settings
  updateNotificationSettings: (settings: Partial<NotificationSettings>) => Promise<void>;
  
  // Integration Settings
  updateIntegrationSettings: (settings: Partial<IntegrationSettings>) => Promise<void>;
  testEmailConfiguration: (settings: any) => Promise<boolean>;
  testSMSConfiguration: (settings: any) => Promise<boolean>;
  
  // Document Templates
  updateDocumentTemplates: (templates: Partial<DocumentTemplates>) => Promise<void>;
  
  // Backup Settings
  updateBackupSettings: (settings: Partial<BackupSettings>) => Promise<void>;
  
  // Staff Management
  updateStaffMember: (memberId: string, updates: Partial<StaffMember>) => Promise<void>;
  addStaffMember: (staffData: { email: string; first_name: string; last_name: string; role: string; department?: string; }) => Promise<{ success: boolean; message: string; data?: any }>;
  
  // UI State
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  successMessage: string | null;
  errorMessage: string | null;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { user } = useAuth();

  // Cache to prevent duplicate loads - keyed by user ID
  const lastLoadedRef = useRef<{ userId: string | null; timestamp: number }>({
    userId: null,
    timestamp: 0
  });
  const CACHE_DURATION = 30000; // 30 seconds cache

  // Get organization ID from user context or settings
  const getOrganizationId = async (): Promise<string | null> => {
    // If we already have it in settings, use it
    if (settings?.organization?.id) {
      return settings.organization.id;
    }

    // Get from user object directly (already loaded by AuthContext) - no database query needed!
    if (!user) return null;

    // User object should have organization_id already from auth context
    if (user.organization_id) {
      return user.organization_id;
    }

    // Only fall back to database query if absolutely necessary (shouldn't happen in normal flow)
    try {
      console.warn('[SettingsContext] Had to fallback to database query for organization_id - this should not happen');
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      return userData?.organization_id || null;
    } catch (error) {
      console.error('Failed to get organization ID:', error);
      return null;
    }
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setErrorMessage(null);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const showErrorMessage = (message: string) => {
    setErrorMessage(message);
    setSuccessMessage(null);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  const refreshSettings = async (force: boolean = false) => {
    if (!user) return;

    // Check cache - if same user and within cache duration, skip reload
    const now = Date.now();
    const isCacheValid = !force &&
      lastLoadedRef.current.userId === user.id &&
      (now - lastLoadedRef.current.timestamp) < CACHE_DURATION;

    if (isCacheValid && settings) {
      console.log('[SettingsContext] Using cached settings, skipping reload');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orgId = await getOrganizationId();
      if (!orgId) {
        // Don't try to load settings if we don't have an organization ID
        console.log('No organization ID available, skipping settings load');
        return;
      }
      console.log('[SettingsContext] Loading settings from database...');
      const settingsData = await settingsService.getAllSettings(orgId);
      setSettings(settingsData);

      // Update cache
      lastLoadedRef.current = {
        userId: user.id,
        timestamp: now
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load settings';
      setError(errorMsg);
      showErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Organization/Business Information
  const updateOrganization = async (updates: OrganizationUpdate) => {
    try {
      setLoading(true);
      const updatedOrg = await settingsService.updateOrganization(updates);
      
      if (settings) {
        setSettings({
          ...settings,
          organization: updatedOrg
        });
      }
      
      showSuccessMessage('Business information updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update business information';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // System Preferences
  const updateSystemPreferences = async (preferences: Partial<SystemPreferences>) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      await settingsService.updateSystemPreferences(orgId, preferences);
      
      if (settings) {
        setSettings({
          ...settings,
          system_preferences: {
            ...settings.system_preferences,
            ...preferences
          }
        });
      }
      
      showSuccessMessage('System preferences updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update system preferences';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Security Settings
  const updateSecuritySettings = async (securitySettings: Partial<SecuritySettings>) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      await settingsService.updateSecuritySettings(orgId, securitySettings);
      
      if (settings) {
        setSettings({
          ...settings,
          security_settings: {
            ...settings.security_settings,
            ...securitySettings
          }
        });
      }
      
      showSuccessMessage('Security settings updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update security settings';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Notification Settings
  const updateNotificationSettings = async (notificationSettings: Partial<NotificationSettings>) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      await settingsService.updateNotificationSettings(orgId, notificationSettings);
      
      if (settings) {
        setSettings({
          ...settings,
          notification_settings: {
            ...settings.notification_settings,
            ...notificationSettings
          }
        });
      }
      
      showSuccessMessage('Notification settings updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update notification settings';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Integration Settings
  const updateIntegrationSettings = async (integrationSettings: Partial<IntegrationSettings>) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      await settingsService.updateIntegrationSettings(orgId, integrationSettings);
      
      if (settings) {
        setSettings({
          ...settings,
          integration_settings: {
            ...settings.integration_settings,
            ...integrationSettings
          }
        });
      }
      
      showSuccessMessage('Integration settings updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update integration settings';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const testEmailConfiguration = async (emailSettings: any): Promise<boolean> => {
    try {
      const result = await settingsService.testEmailConfiguration(emailSettings);
      if (result) {
        showSuccessMessage('Email configuration test successful');
      }
      return result;
    } catch (err) {
      showErrorMessage('Email configuration test failed');
      return false;
    }
  };

  const testSMSConfiguration = async (smsSettings: any): Promise<boolean> => {
    try {
      const result = await settingsService.testSMSConfiguration(smsSettings);
      if (result) {
        showSuccessMessage('SMS configuration test successful');
      }
      return result;
    } catch (err) {
      showErrorMessage('SMS configuration test failed');
      return false;
    }
  };

  // Document Templates
  const updateDocumentTemplates = async (templates: Partial<DocumentTemplates>) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      await settingsService.updateDocumentTemplates(orgId, templates);
      
      if (settings) {
        setSettings({
          ...settings,
          document_templates: {
            ...settings.document_templates,
            ...templates
          }
        });
      }
      
      showSuccessMessage('Document templates updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update document templates';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Backup Settings
  const updateBackupSettings = async (backupSettings: Partial<BackupSettings>) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      await settingsService.updateBackupSettings(orgId, backupSettings);
      
      if (settings) {
        setSettings({
          ...settings,
          backup_settings: {
            ...settings.backup_settings,
            ...backupSettings
          }
        });
      }
      
      showSuccessMessage('Backup settings updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update backup settings';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Staff Management
  const updateStaffMember = async (memberId: string, updates: Partial<StaffMember>) => {
    try {
      setLoading(true);
      await settingsService.updateStaffMember(memberId, updates);
      
      if (settings) {
        const updatedStaff = settings.staff_members.map(member =>
          member.id === memberId ? { ...member, ...updates } : member
        );
        
        setSettings({
          ...settings,
          staff_members: updatedStaff
        });
      }
      
      showSuccessMessage('Staff member updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update staff member';
      showErrorMessage(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addStaffMember = async (staffData: { email: string; first_name: string; last_name: string; role: string; department?: string; }) => {
    try {
      setLoading(true);
      const orgId = await getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID available');
      }
      
      const result = await settingsService.addStaffMember(orgId, staffData);
      
      if (result.success) {
        // Refresh settings to get updated staff list (force refresh)
        await refreshSettings(true);
        showSuccessMessage(result.message);
      } else {
        showErrorMessage(result.message);
      }
      
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to add staff member';
      showErrorMessage(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Load settings on mount and when user changes
  useEffect(() => {
    if (user) {
      refreshSettings();
    }
  }, [user]);

  const contextValue: SettingsContextType = {
    settings,
    loading,
    error,
    refreshSettings,
    updateOrganization,
    updateSystemPreferences,
    updateSecuritySettings,
    updateNotificationSettings,
    updateIntegrationSettings,
    testEmailConfiguration,
    testSMSConfiguration,
    updateDocumentTemplates,
    updateBackupSettings,
    updateStaffMember,
    addStaffMember,
    showSuccessMessage,
    showErrorMessage,
    successMessage,
    errorMessage
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}